import { ICountry, ICountryData } from '../country';
import { ICustomerPricing, ICustomerPricingData } from '../customerpricing';
import {
  IDocument,
  IDocumentData,
  IDocumentTag,
  IDocumentTagData
} from '../document';
import {
  QuoteClassificationEnum,
  QuoteSurchargeApplicationTypeEnum
} from './quote.constants';

// API Data

export interface IQuotePackageData {
  id?: number;
  package_documents?: IQuotePackageDocumentData[];
  quotes?: IQuoteData[];
  created_at?: Date | string;
  updated_at?: Date | string;
}

export interface IQuotePackage extends IQuotePackageData {
  package_documents: IQuotePackageDocument[];
  quotes: IQuote[];
  created_at?: Date;
  updated_at?: Date;
}

export interface IQuotePackageDocumentData {
  id?: number;
  quote_document?: number | IQuoteDocumentData;
  quote_package?: number;
  order?: number;
  section?: number;
}

export interface IQuotePackageDocument extends IQuotePackageDocumentData {
  quote_document: number | IQuoteDocument;
  order: number;
  section: number;
}

export interface IQuoteProductDocumentData {
  id?: number;
  quote_document?: IQuoteDocumentData;
}

export interface IQuoteDocumentData {
  id?: number;
  document?: IDocumentData;
}

export interface IQuoteProductDocument extends IQuoteProductDocumentData {
  quote_document: IQuoteDocument;
}

export interface IQuoteDocument extends IQuoteDocumentData {
  document: IDocument;
}

export interface IQuoteDocumentTagData {
  id?: number;
  tag?: IDocumentTagData;
}

export interface IQuoteDocumentTag extends IQuoteDocumentTagData {
  tag: IDocumentTag;
}

export interface IQuoteServiceItemData {
  id?: number;
  title: string;
  description: string;
  price: string | number;
  days?: string | number;
  template: number | null;
  modified?: boolean;
  price_computation: number | null;
}

export interface IQuoteFreightEstimatesData {
  twenty_feet_containers_quantity?: number;
  forty_feet_containers_quantity?: number;
  forty_feet_containers_hc_quantity?: number;
  fifty_three_feet_trucks_quantity?: number;
  flat_bed_trucks_quantity?: number;
  conestoga_trucks_quantity?: number;
  flat_rack_quantity?: number;
}

export interface IQuoteFreightItemData {
  id?: number;
  title: string;
  description: string;
  location?: string;
  price: string | number;
  template: number | null;
  estimates?: IQuoteFreightEstimatesData | null;
}

export interface IQuoteExtraItemData {
  id?: number;
  title: string;
  description: string;
  price: string | number;
}

export interface IQuoteTermItemData {
  id?: number;
  title: string;
  description: string;
  order: number;
  template: number | null;
}

export interface IQuotePaymentTermItemData {
  id?: number;
  title: string;
  description: string;
  template: number | null;
}

export interface IQuoteProductItemData {
  id?: number;
  module?: number;
  description?: string;
  configuration?: number;
  price?: number;
  quantity?: number;
}

export interface IQuoteProductCategoryData {
  id?: number;
  module: number;
  price?: number;
  discount?: number;
  global_price?: number;
  global_discount?: number;
  accessory_price?: number;
  accessory_discount?: number;
  global_discount_percentage?: number;
  accessory_discount_percentage?: number;
  first_year_subscription_discount_percentage?: number;
  first_year_subscription_discount?: number;
  effective_accessory_discount_percentage?: number;
  effective_core_discount_percentage?: number;
  products?: IQuoteProductItemData[];
}

export interface IQuoteAddressData {
  id?: number;
  address1?: string;
  address2?: string;
  address3?: string;
  address4?: string;
  city?: string;
  region?: string;
  postal_code?: string;
  country?: string;
  country_code?: string | ICountryData;
}

export interface IQuoteContactData {
  id?: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
}

export interface IQuoteCustomerPricingData {
  snapshot?: number;
  customer?: string;
  validity?: string;
}
export interface IQuoteCustomerData {
  id?: number;
  name?: string;
  identifier?: string;
  tax_id?: string;
  address?: IQuoteAddressData;
  ship_to_name?: string;
  ship_address?: IQuoteAddressData;
  contact?: IQuoteContactData;
  is_same_billing_address: boolean;
  contract?: null | ICustomerPricingData;
}

export interface IQuoteVentilationCapacityData {
  quote?: number;

  incubation_chiller_capacities_kw: number | null;
  ventilation_chiller_capacities_kw: number | null;
  incubation_boiler_capacities_kw: number | null;
  ventilation_boiler_capacities_kw: number | null;
  summer_temp_c: number | null;
  summer_temp_f: number | null;
  winter_temp_c: number | null;
  winter_temp_f: number | null;
  summer_rh: number | null;
  winter_rh: number | null;
  voltage_phase_frequency?: string;
  boiler_type?: string;

  ventilation_chiller_capacities_ton?: number | null;
  incubation_chiller_capacities_ton?: number | null;
  ventilation_boiler_capacities_mbh?: number | null;
  incubation_boiler_capacities_mbh?: number | null;
}
export interface IQuoteVentilationCapacity
  extends IQuoteVentilationCapacityData {
  validate(): boolean;
}
export interface IQuoteVentilationRoomData {
  id?: number;
  quote: number;
  description?: string;
}
export interface IQuoteVentilationRoom extends IQuoteVentilationRoomData {
  validate(): boolean;
}

export interface IQuoteData {
  id?: number;
  number?: number;
  revision?: number;
  owner?: string | number;
  language?: string;
  state?: QuoteState;
  type?: number;
  subtype?: number;
  layout?: string;
  project?: string;
  validity_date_range?: number;
  exchange_rate?: string | number;
  currency?: number;
  amount?: number;
  customer?: IQuoteCustomerData;
  latest?: boolean;
  agent?: string | number;
  inside_sales_owner?: string | number;
  comments?: string;
  notes?: string;
  region?: number | null;
  salesforce_opportunity_id?: string | null;

  equipment_nettotal?: number;
  extras_subtotal?: number;
  freight_subtotal?: number;
  services_subtotal?: number;

  equipment_subtotal?: number;
  subscription_first_year_discount?: number;
  subscription_yearly_subtotal?: number;
  surcharge?: number;
  surcharge_discount_percent?: number;
  net_surcharge?: number;
  surcharge_discount?: number;
  // null because we cannot set initial value as new Date but is mandatory
  rfq_date?: Date | string | null;
  rfq_number?: string | null;
  rfq_attachment?: null | string;
  validity_start_date?: Date | string;

  project_size?: IQuoteProjectSizeData;

  // list items
  categories?: IQuoteProductCategoryData[];
  service_items?: IQuoteServiceItemData[];
  freight_items?: IQuoteFreightItemData[];
  extra_items?: IQuoteExtraItemData[];
  terms?: IQuoteTermItemData[];
  payment_terms?: IQuotePaymentTermItemData[];

  // discounts
  ewg_discount?: number;
  ewg_discount_percent?: number;

  special_discount?: number;
  special_discount_percent?: number;

  commission?: number;
  commission_percent?: number;

  effective_blended_discount_percentage?: number;
  target_discount_percent?: number;

  effective_accessory_discount_percentage?: number;
  effective_core_discount_percentage?: number;

  effective_equipment_discount_percentage?: number;
  highest_approval_level?: number;

  customer_relationship_owner?: string;
  bidding_environment?: string;
  classification?: null | number;

  is_budgetary?: boolean;
  surcharge_application_type?: number;

  // ventilation
  capacity?: IQuoteVentilationCapacityData | null;
  rooms?: IQuoteVentilationRoomData[];

  // Branding
  organization?: number;

  is_test?: boolean;

  created_at?: Date | string;
  updated_at?: Date | string;

  package?: number | null;
}
export interface IQuoteFieldErrorData {
  number?: string[];
  revision?: string[];
  owner?: string[];
  language?: string[];
  state?: string[];
  type?: string[];
  subtype?: string[];
  layout?: string[];
  project?: string[];
  validity_date_range?: string[];
  exchange_rate?: string[];
  currency?: string[];
  customer?: IQuoteCustomerFieldErrorData;
  latest?: string[];
  agent?: string[];
  inside_sales_owner?: string[];
  comments?: string[];
  notes?: string[];
  region?: string[];
  rfq_date?: string[];
  rfq_number?: string[];

  is_budgetary?: string[];
  // !list items
  // categories: IQuoteProductCategoryData[];
  // service_items: IQuoteServiceItemData[];
  // freight_items: IQuoteFreightItemData[];
  // extra_items: IQuoteExtraItemData[];
  // terms: IQuoteTermItemData[];
  // payment_terms: IQuotePaymentTermItemData[];

  // !discounts
  // ewg_discount?: number;
  // ewg_discount_percent?: number;

  // special_discount?: number;
  // special_discount_percent?: number;

  // commission?: number;
  // commission_percent?: number;

  capacity?: IQuoteVentilationCapacityFieldErrorData;
  rooms?: IQuoteVentilationRoomFieldErrorData[];
}

export interface IQuoteOrganization {
  value: QuoteOrganizationEnum;
  display_name: string;

  toString(): string;
}

export interface IQuoteClassification {
  value: QuoteClassificationEnum;
  display_name: string;

  toString(): string;
}

export interface IQuoteCustomerFieldErrorData {
  name?: string[];
  identifier?: string[];
  tax_id?: string[];
  is_same_billing_address?: string[];

  address?: IQuoteAddressFieldErrorData;
  ship_to_name?: string[];
  ship_address?: IQuoteAddressFieldErrorData;
  contact?: IQuoteContactFieldErrorData;
}

export interface IQuoteAddressFieldErrorData {
  address1?: string[];
  address2?: string[];
  address3?: string[];
  address4?: string[];
  city?: string[];
  region?: string[];
  postal_code?: string[];
  country?: string[];
  country_code?: string[];
}
export interface IQuoteContactFieldErrorData {
  first_name?: string[];
  last_name?: string[];
  email?: string[];
  phone?: string[];
}

export interface IQuoteVentilationCapacityFieldErrorData {
  quote?: string[];
  incubation_chiller_capacities_kw?: string[];
  ventilation_chiller_capacities_kw?: string[];
  incubation_boiler_capacities_kw?: string[];
  ventilation_boiler_capacities_kw?: string[];
  summer_temp_c?: string[];
  summer_temp_f?: string[];
  winter_temp_c?: string[];
  winter_temp_f?: string[];
  summer_rh?: string[];
  winter_rh?: string[];
  voltage_phase_frequency?: string[];
  boiler_type?: string[];
  ventilation_chiller_capacities_ton?: string[];
  incubation_chiller_capacities_ton?: string[];
  ventilation_boiler_capacities_mbh?: string[];
  incubation_boiler_capacities_mbh?: string[];
}

export interface IQuoteVentilationRoomFieldErrorData {
  quote?: string[];
  description?: string[];
}

// Business Logic Interface
export interface IQuoteProductCategory extends IQuoteProductCategoryData {
  products?: IQuoteProductItem[];

  validate(): boolean;
}

export interface IQuoteServiceItem extends IQuoteServiceItemData {
  price: number;
  validate(): boolean;
}
export interface IQuoteFreightEstimates extends IQuoteFreightEstimatesData {
  validate(
    data: IQuoteFreightEstimatesData,
    requiredFields: (keyof IQuoteFreightEstimatesData)[]
  ): void;
}
export interface IQuoteFreightItem extends IQuoteFreightItemData {
  price: number;
  estimates: IQuoteFreightEstimates | null;

  validate(): boolean;
}

export interface IQuoteExtraItem extends IQuoteExtraItemData {
  price: number;
  validate(): boolean;
}

export interface IQuotePaymentTermItem extends IQuotePaymentTermItemData {
  validate(): boolean;
}

export interface IQuoteTermItem extends IQuoteTermItemData {
  validate(): boolean;
}

export interface IQuoteProductItem extends IQuoteProductItemData {
  isEqual(product: IQuoteProductItem): boolean;

  validate(): boolean;
}

export interface IQuoteProductCategory extends IQuoteProductCategoryData {
  addProduct(product: IQuoteProductItem): void;
  updateProduct(product: IQuoteProductItem): void;
  removeProduct(product: IQuoteProductItem): void;

  validate(): boolean;
}

export interface IQuoteAddress extends IQuoteAddressData {
  country_code: ICountry | string;
  validate(): boolean;
}

export interface IQuoteContact extends IQuoteContactData {
  validate(): boolean;
}

export interface IQuoteCustomerPricing extends IQuoteCustomerPricingData {
  validate(): boolean;
}
export interface IQuoteCustomer extends IQuoteCustomerData {
  address?: IQuoteAddress;
  ship_address?: IQuoteAddress;
  contact?: IQuoteContact;
  contract: null | ICustomerPricing;
}

export interface IQuoteProjectSizeData {
  value: number;
  type: string;
}
export interface IQuoteProjectSize extends IQuoteProjectSizeData {
  toString(): string;
}

export interface IQuote extends IQuoteData {
  highest_approval_level: number;
  surcharge: number;
  surcharge_discount_percent: number;
  net_surcharge: number;
  surcharge_discount: number;
  exchange_rate?: number;
  customer?: IQuoteCustomer;
  state: QuoteState;
  salesforce_opportunity_id: string | null;

  equipment_subtotal: number;
  subscription_first_year_discount: number;
  subscription_yearly_subtotal: number;

  categories: IQuoteProductCategory[];
  service_items: IQuoteServiceItem[];
  freight_items: IQuoteFreightItem[];
  extra_items: IQuoteExtraItem[];
  terms: IQuoteTermItem[];
  payment_terms: IQuotePaymentTermItem[];

  // ventilations
  capacity: IQuoteVentilationCapacity | null;
  rooms: IQuoteVentilationRoom[];
  rfq_date: Date | string | null;
  rfq_number: string | null;

  expiry_date: Date;
  surcharge_application_type: QuoteSurchargeApplicationTypeEnum;

  // Branding
  organization: number;

  addVentilationRoom(room: IQuoteVentilationRoom): void;
  updateVentilationRoom(room: IQuoteVentilationRoom): void;
  removeVentilationRoom(room: IQuoteVentilationRoom): void;

  addProductItem(product: IQuoteProductItem): void;
  updateProductItem(product: IQuoteProductItem): void;
  removeProductItem(product: IQuoteProductItem): void;
  getAllProducts(): IQuoteProductItem[];

  getOrganization(): IQuoteOrganization;
  getClassification(): IQuoteClassification | null;

  isMachineQuote(): boolean;
  isAutomationQuote(): boolean;
  isVentilationQuote(): boolean;

  isExpired(): boolean;

  isTariffsApplicable(): boolean;

  canCreateSalesOpportunity(): boolean;

  validate(): boolean;
  toString(): string;
  toCustomerProposalString(): string;
}

export interface IQuoteFieldError extends IQuoteFieldErrorData {
  customer: IQuoteCustomerFieldError;
  capacity: IQuoteVentilationCapacityFieldError;
  rooms: IQuoteVentilationRoomFieldError[];
  clearErrors(): void;
}
export interface IQuoteCustomerFieldError extends IQuoteCustomerFieldErrorData {
  ship_address: IQuoteAddressFieldError;
  address: IQuoteAddressFieldError;
  contact: IQuoteContactFieldError;
  clearErrors(): void;
}
export interface IQuoteContactFieldError extends IQuoteContactFieldErrorData {
  clearErrors(): void;
}
export interface IQuoteAddressFieldError extends IQuoteAddressFieldErrorData {
  clearErrors(): void;
}
export interface IQuoteVentilationCapacityFieldError
  extends IQuoteVentilationCapacityFieldErrorData {
  clearErrors(): void;
}
export interface IQuoteVentilationRoomFieldError
  extends IQuoteVentilationRoomFieldErrorData {
  clearErrors(): void;
}

// Constants - Used for validation
export enum QuoteState {
  DRAFT,
  SUBMITTED,
  IN_REVIEW,
  APPROVAL_2_PENDING,
  APPROVAL_3_PENDING,
  APPROVAL_4_PENDING,
  APPROVED,
  CANCELLED,
  PREPARE_FOR_CUSTOMER,
  OUT_TO_CUSTOMER,
  AWARDED,
  LOST,
  ORDER_CREATED,
  REVIEWING_DISCOUNTS,
  REVIEWING_SURCHARGES
}

export enum QuoteAirwayVentilationSubType {
  CC3 = 1,
  MODULAR = 2,
  ART = 3
}
export enum QuoteType {
  MACHINE_PROJECTS = 0,
  AFTER_MARKET_PARTS = 1,
  RTU_VENTILATION = 2,
  AIRWAY_VENTILATION = 3,
  AUTOMATION = 4
}

export enum QuoteOrganizationEnum {
  Jamesway,
  Jamesway_SEA
}

export enum QuotePackageSectionEnum {
  Cover = 1,
  SpecSheets
}
